import { NavLink } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  Calendar, 
  Trophy, 
  CreditCard,
  FileText,
  MessageSquare,
  LogOut,
  X,
  UserCog,
  KeyRound,
  ClipboardList,
  Clock,
  HeadphonesIcon,
  CalendarCheck,
  Calculator,
  UsersRound
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { canAccessMenu } from '../../config/roles';

// All nav items with their menu keys for permission checking
const allNavItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', menuKey: 'dashboard' },
  { to: '/employees', icon: UserCog, label: 'Employees', menuKey: 'employees' },
  { to: '/tasks', icon: ClipboardList, label: 'Tasks', menuKey: 'tasks' },
  { to: '/timesheets', icon: Clock, label: 'Timesheets', menuKey: 'timesheets' },
  { to: '/monthly-attendance', icon: CalendarCheck, label: 'Monthly Attendance', menuKey: 'monthly-attendance' },
  { to: '/salary-calculation', icon: Calculator, label: 'Salary Calculation', menuKey: 'salary-calculation' },
  { to: '/password-requests', icon: KeyRound, label: 'Password Requests', menuKey: 'password-requests' },
  { to: '/users', icon: Users, label: 'Users', menuKey: 'users' },
  { to: '/groups', icon: UsersRound, label: 'Groups', menuKey: 'groups' },
  { to: '/events', icon: Calendar, label: 'Events', menuKey: 'events' },
  { to: '/matches', icon: Trophy, label: 'Matches', menuKey: 'matches' },
  { to: '/payments', icon: CreditCard, label: 'Payments', menuKey: 'payments' },
  { to: '/invoices', icon: FileText, label: 'Invoices', menuKey: 'invoices' },
  { to: '/tickets', icon: MessageSquare, label: 'Support Tickets', menuKey: 'tickets' },
  { to: '/customer-support', icon: HeadphonesIcon, label: 'Customer Support Chat', menuKey: 'customer-support' },
];

export function Sidebar({ open, onClose }) {
  const { logout, user } = useAuth();
  
  // Get user's role, default to 'admin' if not set
  const userRole = user?.role || 'admin';
  
  // Filter nav items based on user's role
  const navItems = allNavItems.filter(item => canAccessMenu(userRole, item.menuKey));

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside className={cn(
        "fixed top-0 left-0 z-50 h-full w-64 bg-[#0f172a] border-r border-slate-800 transition-transform duration-300 lg:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full"
      )}>
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <img 
              src="/logo.png" 
              alt="Lunipin" 
              className="w-8 h-8 rounded-full object-cover"
            />
            <span className="font-bold text-lg tracking-tight text-white">Lunipin Admin</span>
          </div>
          <button 
            onClick={onClose}
            className="lg:hidden p-1 hover:bg-slate-800 rounded"
            data-testid="close-sidebar-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto max-h-[calc(100vh-140px)]" data-testid="sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onClose}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm font-medium transition-all",
                isActive 
                  ? "bg-green-500/10 text-green-400 border-l-2 border-green-400" 
                  : "text-slate-400 hover:text-white hover:bg-slate-800"
              )}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="flex items-center gap-3 w-full px-4 py-2.5 rounded-sm text-sm font-medium text-red-400 hover:bg-red-500/10 transition-all"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </aside>
    </>
  );
}
